import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { usePageScale } from "@/ui/usePageScale";
import { PageShell } from "@/ui/PageShell";
import { withBase } from "@/ui/Header";
import {customConfirm} from "@/ui/confirm";
import { COLOR, FW, BORDER } from "@/ui/tokens";

const AUTH_BASE = (import.meta.env.VITE_AUTH_BASE ?? import.meta.env.VITE_API_BASE ?? "").replace(/\/+$/, "");

const BASE = {
    W: 1264,
    MARGIN_X: 108,
    GAP_HDR_TO_FIRST: 40,

    INPUT_W: 1048,
    INPUT_H: 124,
    INPUT_FZ: 35,
    ROW_GAP: 64,

    BTN_W: 724,
    BTN_H: 124,
    BTN_FZ: 40,
    GAP_TO_BTN: 140,
} as const;

function useScale() {
    const { s, px, sfpx } = usePageScale({ baseW: BASE.W, max: 1.2 });
    const spx = px;
    return { s, spx, sfpx };
}

export default function ResetPasswordPage() {
    const { s, spx, sfpx } = useScale();
    const nav = useNavigate();
    const loc = useLocation() as any;

    const [resetToken, setResetToken] = useState<string | null>(null);
    const [pw, setPw] = useState("");
    const [pw2, setPw2] = useState("");
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState<string | null>(null);
    const [msg, setMsg] = useState<string | null>(null);

    useEffect(() => {
        const t = String(loc.state?.reset_token || "") || sessionStorage.getItem("bt.pwreset.token");
        if (!t) {
            nav("/auth/login", { replace: true });
            return;
        }
        setResetToken(t);
    }, [loc.state, nav]);

    const pwPolicyOk = useMemo(() => /^(?=.*[A-Za-z])(?=.*\d).{4,20}$/.test(pw), [pw]);
    const pwMatchOk = useMemo(() => pw2 === pw && pw.length > 0, [pw, pw2]);
    const canSubmit = !!resetToken && pwPolicyOk && pwMatchOk && !busy;

    const Label = ({ children }: { children: React.ReactNode }) => (
        <div style={{ fontSize: sfpx(36), fontWeight: 900, color: COLOR.text, marginBottom: spx(12) }}>{children}</div>
    );

    const onSubmit = async () => {
        if (!resetToken) return;

        setBusy(true);
        setErr(null);
        setMsg(null);

        try {
            const r = await fetch(`${AUTH_BASE}/password/reset`, {
                method: "POST",
                headers: { "Content-Type": "application/json", Accept: "application/json" },
                body: JSON.stringify({ reset_token: resetToken, new_password: pw }),
            });

            const data = (await r.json().catch(() => ({}))) as any;
            if (!r.ok) {
                const code = String(data?.error || "");
                if (code === "INVALID_PASSWORD_POLICY") {
                    setErr("비밀번호는 4~20자, 영문+숫자를 포함해야 합니다.");
                    return;
                }
                if (code === "TOKEN_ALREADY_USED") {
                    setErr("요청이 만료되었거나 이미 사용되었습니다. 다시 시도해주세요.");
                    return;
                }
                setErr("비밀번호 재설정에 실패했습니다. 다시 시도해주세요.");
                return;
            }

            sessionStorage.removeItem("bt.pwreset.token");
            const u = sessionStorage.getItem("bt.pwreset.username") || "";
            sessionStorage.removeItem("bt.pwreset.username");
            await customConfirm("비밀번호가 성공적으로 변경되었습니다. 로그인해주세요.", "확인");
            nav("/auth/login", { replace: true, state: { prefill_username: u } });
        } catch {
            setErr("비밀번호 재설정에 실패했습니다. 다시 시도해주세요.");
        } finally {
            setBusy(false);
        }
    };

    return (
        <PageShell
            title="비밀번호 찾기"
            onBack={() => nav(-1)}
            backIcon={withBase("icons/prev1.svg")}
            width={spx(BASE.W)}
            bodyStyle={{ paddingLeft: spx(BASE.MARGIN_X), paddingRight: spx(BASE.MARGIN_X) }}
        >
            <div style={{ height: spx(BASE.GAP_HDR_TO_FIRST) }} />

            <Label>비밀번호</Label>
            <input
                placeholder="비밀번호 입력"
                type="password"
                autoComplete="new-password"
                value={pw}
                onChange={(e) => setPw(e.target.value)}
                style={{
                    width: spx(BASE.INPUT_W),
                    height: spx(BASE.INPUT_H),
                    borderRadius: s(12),
                    padding: `0 ${spx(16)}`,
                    border: BORDER.boldDark,
                    fontSize: sfpx(BASE.INPUT_FZ),
                    fontWeight: 900,
                }}
            />
            <div style={{ marginTop: spx(12), fontSize: sfpx(20), color: COLOR.text }}>4~20자 / 영문+숫자 포함 / 특수문자 사용 가능</div>

            <div style={{ height: spx(BASE.ROW_GAP) }} />

            <Label>비밀번호 확인</Label>
            <input
                placeholder="비밀번호 확인"
                type="password"
                autoComplete="new-password"
                value={pw2}
                onChange={(e) => setPw2(e.target.value)}
                style={{
                    width: spx(BASE.INPUT_W),
                    height: spx(BASE.INPUT_H),
                    borderRadius: s(12),
                    padding: `0 ${spx(16)}`,
                    border: BORDER.boldDark,
                    fontSize: sfpx(BASE.INPUT_FZ),
                    fontWeight: 900,
                }}
            />

            {(err || msg) && (
                <>
                    <div style={{ height: spx(16) }} />
                    <div role="status" style={{ fontSize: sfpx(20), color: err ? COLOR.error : COLOR.success }}>
                        {err || msg}
                    </div>
                </>
            )}

            <div style={{ height: spx(BASE.GAP_TO_BTN) }} />
            <div style={{ display: "grid", placeItems: "center" }}>
                <button
                    type="button"
                    onClick={onSubmit}
                    disabled={!canSubmit}
                    style={{
                        width: spx(BASE.BTN_W),
                        height: spx(BASE.BTN_H),
                        borderRadius: s(12),
                        background: COLOR.black,
                        color: COLOR.white,
                        fontSize: sfpx(BASE.BTN_FZ),
                        fontWeight: 900,
                        border: "none",
                        opacity: !canSubmit ? 0.75 : 1,
                    }}
                >
                    {busy ? "처리 중…" : "비밀번호 재설정"}
                </button>
            </div>

            <div style={{ height: spx(40) }} />
        </PageShell>
    );
}
